// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRequest } from "ahooks";
import { toast } from "react-toastify";
import { Loader } from "@mantine/core";
import { Storage } from "aws-amplify";

import { ImageFile, Position, PositionImage, UserOrder } from "../../interfaces";
import DangerModal from "../dialogs/DangerModal";
import useOrderPosition from "../../hooks/useOrderPosition";
import { checkOrder } from "../../api";
import { isEditableCustomerView } from "../../utils";
import { useUser } from "../../contexts";
import useOrder from "../../hooks/useOrder";

import PositionItem from "./PositionItem";
import { ArrowIcon, CheckGreenIcon } from "../../assets/svgs";

type Props = {
    setImagesDataTwo: (data: PositionImage[]) => void;
    setButtonStatus: (isEnable: boolean) => void;
};

const AccountPosition: React.FC<Props> = ({ setImagesDataTwo, setButtonStatus }) => {
    const { t } = useTranslation();
    const { positionImageAvailable } = useOrderPosition();
    const [{ order }] = useUser();
    const { handleOrderUpdate } = useOrder();

    const [sent] = useState(false);
    const [imagesData, setImagesData] = useState<PositionImage[]>([]);

    const {
        data,
        loading: fetching,
        mutate,
    } = useRequest<UserOrder>(() => checkOrder(), {
        ready: order?.id !== "not_found",
    });

    /**
     * Total required (ohne White) – derived, kein Effect, kein "var"
     */
    const totalItemRequired = useMemo(() => {
        return (data?.positions ?? []).reduce((sum, p) => {
            if (p.White) return sum;
            return sum + (p.quantity ?? 0);
        }, 0);
    }, [data?.positions]);

    /**
     * Total uploaded quantity (über alle PositionsImages) – derived
     */
    const totalUploadedQuantity = useMemo(() => {
        let total = 0;
        for (const p of data?.positions ?? []) {
            for (const img of p.positionImage ?? []) total += img.quantity ?? 0;
        }
        return total;
    }, [data?.positions]);

    /**
     * Button-Enablement als derived rule (kein Sideeffect-Loop, keine Tick-Hacks)
     * Wenn du "White" anders behandeln willst, sag Bescheid.
     */
    const allRequiredUploaded = useMemo(() => {
        if (!data) return false;
        return totalItemRequired > 0 && totalUploadedQuantity >= totalItemRequired;
    }, [data, totalItemRequired, totalUploadedQuantity]);

    useEffect(() => {
        setButtonStatus(allRequiredUploaded);
    }, [allRequiredUploaded, setButtonStatus]);

    /**
     * lastOpened nur dann updaten, wenn die Order-ID da ist (und idealerweise nur 1x pro Order)
     * (Wenn du wirklich auf email reagieren willst: deps um order?.email erweitern.)
     */
    useEffect(() => {
        if (!order?.id) return;
        // Fire-and-forget, aber mit Catch damit keine unhandled rejection rumfliegt
        void handleOrderUpdate({ id: order.id, lastOpened: order.email }).catch(() => {
            // bewusst still, lastOpened ist nicht kritisch
        });
    }, [order?.id]); // absichtlich nur ID

    /**
     * Images Change: Wir halten lokalen UI-State für "aktuelles Item" + geben an Parent weiter.
     * Den Button-Status leiten wir aber aus "data" ab, nicht aus "order" oder lokalen temporären Arrays.
     */
    const handleImagesChange = useCallback(
        async (updatedImages: PositionImage[], index: number) => {
            setImagesData(updatedImages);
            setImagesDataTwo(updatedImages);

            mutate(await checkOrder());
        },
        [setImagesDataTwo]
    );

    /**
     * Remove Files: NICHT `order` mutieren. Wir arbeiten mit `data` als Source of Truth.
     * 1) Aus altem State Keys sammeln
     * 2) neue Positions immutabel erstellen
     * 3) Update via API
     * 4) UI über mutate(updated) oder refetch aktualisieren
     * 5) Storage löschen
     */
    const handleRemoveFile = useCallback(
        async (index: number, item: Position) => {
            try {
                if (!order?.id) return;
                if (!data) return;

                const currentPos = data.positions[index];
                const imagesToRemove = (currentPos?.positionImage ?? []).slice();
                const filesToRemove = (order.files ?? []).slice();

                if (imagesToRemove.length === 0) return;

                const nextPositions = data.positions.map((p, i) =>
                    i === index ? { ...p, positionImage: [] } : p
                );

                const updated = await handleOrderUpdate({
                    id: order.id,
                    positions: nextPositions,
                    files: [],
                });

                if (updated) {
                    mutate(updated);
                } else {
                    mutate(await checkOrder());
                }

                await Promise.all([
                    ...imagesToRemove.flatMap((img) => [
                        Storage.remove(img.key),
                        Storage.remove(img.thumbnail),
                    ]),
                    ...filesToRemove.map((f: ImageFile) => Storage.remove(f.key)),
                ]);
            } catch (error) {
                toast.error((error as Error)?.message ?? "We are unable to process this request.");
            }
        },
        [data, handleOrderUpdate, mutate, order?.id]
    );

    const isEditable = useMemo(() => {
        return data ? isEditableCustomerView(data.status) : false;
    }, [data]);

    const handleDangerDelete = useCallback(() => {
        if (!data) return;
        data.positions.forEach((pos, idx) => {
            void handleRemoveFile(idx, pos);
        });
    }, [data, handleRemoveFile]);

    return (
        <div>
            {order?.chooseUpload && !positionImageAvailable() && (
                <DangerModal handleDelete={handleDangerDelete} />
            )}

            {!sent ? (
                <>
                    {!fetching && data && data.id !== "not_found" ? (
                        <div className={`${!isEditable && "opacity-disabled pointer-events-none"}`}>
                            <h3 className="text-xl">{t("positions")}</h3>

                            <div className="position-items">
                                <div
                                    className="position-item__header sticky top-0 z-10 bg-white rounded-md shadow-sm border border-gray-200 px-4 py-3 flex items-center justify-between hide-md"
                                >
                                    <div className="header-item">
                                        <h2>{t("product")}</h2>
                                        <h2>{t("productNumber")}</h2>
                                    </div>

                                    <div className="header-item">
                                        <h2>{t("dimension")}</h2>
                                    </div>

                                    <div className="header-item">
                                        <h2>{t("number")}</h2>
                                    </div>

                                    <div className="header-item not-visible flex items-center justify-center">
                                        <ArrowIcon hide className="transform -scale-y-100" />
                                    </div>
                                </div>

                                {data.positions.map((position, index) => (
                                    <PositionItem
                                        key={index}
                                        item={position}
                                        index={index}
                                        order={data}
                                        mutate={mutate}
                                        disabled={!isEditable}
                                        onImagesChange={(updatedImages) => handleImagesChange(updatedImages, index)}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="row row-center mt-30">
                            <Loader />
                        </div>
                    )}
                </>
            ) : (
                <div className="sent-printdata">
                    <CheckGreenIcon />
                    <span>{t("sendSuccess")}</span>
                    <p className="p-4">{t("thankYouForUploading")}</p>
                    <p className="p-4">{t("noteAfterUpload")}</p>
                </div>
            )}
        </div>
    );
};

export default AccountPosition;